import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import HistorySidebar from './HistorySidebar';
import * as styles from './PostHistoryView.module.scss';
import ResponsiveDate from './ResponsiveDate';
import useHistoryRange from './useHistoryRange';
import VersionChanges from './VersionChanges';
import VersionChangesWall from './VersionChangesWall';
import { useRightPanel } from '../../app/layout/RightPanel';
import IconButton from '../../components/IconButton';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import QuillContext from '../../editor/QuillContext';
import { gql } from '../../graphql/generated/gql';
import arrowLeftIcon from '../../icons/non-prefilled/16x16/arrowLeft.svg';
import isSize from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import useSlug from '../../utils/router/useSlug';
import postPath from '../../utils/url/postPath';

const POST_HISTORY_VIEW_QUERY = gql(/** @complexity 3 */ `
  query PostHistoryView($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      title
      history(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        insertedAt
      }
    }
    entityAssetToken(entityId: $postId, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface InnerPostHistoryViewProps {
  postId: string;
  params: { versionFrom: string; versionTo: string };
}

const InnerPostHistoryView = ({
  postId,
  params,
}: InnerPostHistoryViewProps) => {
  const versionFrom = parseInt(params.versionFrom, 10);
  const versionTo = parseInt(params.versionTo, 10);

  const { data } = useQuery(POST_HISTORY_VIEW_QUERY, {
    variables: {
      postId,
      versionFrom,
      versionTo,
    },
  });

  const post = data?.post;

  const { content, allVersionChanges } = useHistoryRange({
    postId,
    versionFrom,
    versionTo,
  });

  return (
    <VersionChanges.Root allVersionChanges={allVersionChanges}>
      <VersionChanges.Wrapper
        initialTitle="History - Slab"
        postTitle={post?.title}
      >
        <VersionChanges.ContentGrid>
          <VersionChanges.TopbarContainer>
            <VersionChanges.Topbar>
              {post?.history ? (
                <div className={styles.metaContainer}>
                  <IconButton
                    applyNegativeMargin
                    to={postPath(post)}
                    aria-label="Go back to post"
                  >
                    {arrowLeftIcon}
                  </IconButton>
                  <VersionChanges.Meta>
                    <ResponsiveDate
                      date={post.history.insertedAt}
                      render={({ condensed, dateWithPreposition }) => {
                        return condensed
                          ? `Edited ${dateWithPreposition}`
                          : `Version edited ${dateWithPreposition}`;
                      }}
                    />
                  </VersionChanges.Meta>
                </div>
              ) : undefined}
            </VersionChanges.Topbar>
          </VersionChanges.TopbarContainer>
          <VersionChanges.ScrollContainer>
            <VersionChanges.ContentContainer>
              <VersionChanges.Content>
                <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
                  <VersionChanges.Post content={content} />
                </AssetTokenProvider>
              </VersionChanges.Content>
              <VersionChangesWall
                versionFrom={versionFrom}
                versionTo={versionTo}
                postId={postId}
              />
            </VersionChanges.ContentContainer>
          </VersionChanges.ScrollContainer>
        </VersionChanges.ContentGrid>
      </VersionChanges.Wrapper>
    </VersionChanges.Root>
  );
};

const PostHistoryView = () => {
  const [postId] = useSlug('postSlug');
  const params = useParams() as {
    versionFrom: string;
    versionTo: string;
  };

  const { openRightPanel } = useRightPanel();

  useEffect(() => {
    if (!isSize(Sizes.SM)) {
      openRightPanel(<HistorySidebar postId={postId} tab="versions" />);
    }
  }, [openRightPanel, postId]);

  return (
    <QuillContext>
      <InnerPostHistoryView
        key={`${postId}-${params.versionFrom}-${params.versionTo}`}
        postId={postId}
        params={params}
      />
    </QuillContext>
  );
};

export default PostHistoryView;
